import { Text, HStack } from "@chakra-ui/react"
import { ColumnDef } from "@tanstack/react-table"
import { CustomerSite, TenantProfile } from "@hydrologiq/gql-experience/graphql"
import { useNavigate } from "react-router-dom"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { useGetProfile } from "@hydrologiq/shared/providers/helpers/useProfile"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { PaginatedTable, ServerPagination } from "@hydrologiq/shared/components/PaginatedTable"
import { PaginationMeta, SortConfig } from "@hydrologiq/shared/types/pagination"
import Location from "@hydrologiq/shared/attributes/Location"
import SiteRunDryIcon from "./SiteRunDryIcon"
import CustomerSiteHydrogen from "./CustomerSiteHydrogen"
import CustomerAlertsPopup from "../popups/CustomerAlertsPopup"
import CustomerAssetsPopup from "../popups/CustomerAssetsPopup"
import Link from "@hydrologiq/shared/components/Link"
import SystemRunDryIcon from "./SystemRunDryIcon"

interface CustomerSitesPaginatedProps {
  customerId: string
  sites: CustomerSite[]
  loading?: boolean
  error?: string | null
  onRetry?: () => void
  pagination?: PaginationMeta
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  sort?: SortConfig | null
  onSortChange?: (sort: SortConfig | null) => void
  headerActions?: React.ReactNode
}

function CustomerSitesPaginated({
  customerId,
  sites,
  loading,
  error,
  onRetry,
  pagination,
  onPageChange,
  onPageSizeChange,
  sort,
  onSortChange,
  headerActions,
}: CustomerSitesPaginatedProps) {
  const route = useRoute()
  const navigate = useNavigate()
  const profile = useGetProfile()

  const isRentalProfile = profile === TenantProfile.Rental
  const isFuelProducerProfile = profile === TenantProfile.FuelProducer
  const assetDepletionEnabled = useGetFeature("asset_depletion")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.NAME],
    [COPY_KEYS.HEADINGS.ASSETS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSETS],
    [COPY_KEYS.HEADINGS.RUN_DRY]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.RUN_DRY],
    [COPY_KEYS.LABELS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.SITE],
  })

  // Define base columns that are always shown
  const baseColumns: ColumnDef<CustomerSite, any>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: `${copy[COPY_KEYS.LABELS.SITE]} Name`,
      cell: ({ row }) => {
        const site = row.original
        const sitePath = `${route.endsWith("/") ? route : route + "/"}${customerId}/sites/${site.id}`
        return (
          <Tooltip content={`Explore ${site.name}`}>
            <Link
              variant="primary"
              href={sitePath}
              onClick={(e) => {
                e.preventDefault()
                navigate(sitePath)
              }}
            >
              {site.name.length > 35 ? `${site.name.slice(0, 35)}...` : site.name}
            </Link>
          </Tooltip>
        )
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "location",
      header: "Location",
      cell: ({ row }) => {
        const site = row.original
        return <Location name={site.location?.name || site.name} location={site.location as any} />
      },
      enableSorting: false,
      minSize: 200,
    },
    {
      id: "alerts",
      header: "Alerts",
      cell: ({ row }) => {
        const site = row.original
        // Create a mock customer object for the alerts popup
        const mockCustomer = { id: customerId, alertCount: site.alertCount || 0, alerts: site.alerts }
        return <CustomerAlertsPopup customer={mockCustomer as any} alertCount={site.alertCount || 0} />
      },
      enableSorting: false,
    },
  ]

  // Conditionally add asset depletion column (controlled by feature toggle)
  if (assetDepletionEnabled) {
    baseColumns.push({
      id: "run_dry",
      header: () => (
        <Tooltip content="Earliest asset run dry time">
          <HStack justify="start">
            <Text>Asset Depleted in</Text>
          </HStack>
        </Tooltip>
      ),
      cell: ({ row }) => <SiteRunDryIcon site={row.original} customerId={customerId} />,
      enableSorting: false,
    })
  }

  if (isRentalProfile) baseColumns.push(
    {
      id: "installation_run_dry",
      header: () => (
        <Tooltip content="Earliest installation run dry time">
          <HStack justify="center" width="100%">
            <Text>Refuel needed in</Text>
          </HStack>
        </Tooltip>
      ),
      cell: ({ row }) => <SystemRunDryIcon runDryMinutes={row.original.earliestInstallationRunDryMinutes ?? undefined} system={row.original.earliestInstallationRunDrySystem?.name ?? undefined} systemId={row.original.earliestInstallationRunDrySystem?.id ?? undefined} />,
      enableSorting: false,
    })
  baseColumns.push({
    id: "assets",
    header: `${copy[COPY_KEYS.HEADINGS.ASSETS]}`,
    cell: ({ row }) => {
      const site = row.original
      // Create a mock customer object for the assets popup
      return <CustomerAssetsPopup assetCount={site.assetCount || 0} assets={site.assets} />
    },
    enableSorting: false,
  })

  // Build profile-specific columns
  const profileColumns: ColumnDef<CustomerSite, any>[] = []

  if (isFuelProducerProfile) {
    profileColumns.push({
      id: "hydrogen_on_site",
      header: () => (
        <Tooltip content="Total hydrogen currently on site">
          <HStack justify="start">
            <Text>Hydrogen on Site (kg)</Text>
          </HStack>
        </Tooltip>
      ),
      cell: ({ row }) => <CustomerSiteHydrogen site={row.original} />,
      enableSorting: true,
    })
  }

  if (isRentalProfile) {
    // Rental-specific metrics
    profileColumns.push(

      {
        id: "energy",
        accessorKey: "energy",
        header: "Energy Yesterday (kWh)",
        cell: ({ row }) => (
          <Text textAlign="end">{row.original.energy ? `${row.original.energy.toFixed(2)} kWh` : "-"}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "co2e",
        accessorKey: "co2e",
        header: "CO2e Yesterday (kg)",
        cell: ({ row }) => (
          <Text textAlign="end">{row.original.co2e ? `${row.original.co2e.toFixed(2)} kg` : "-"}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      }
    )
  }

  // Combine base columns with profile-specific columns
  const columns = [...baseColumns, ...profileColumns]

  // Setup server pagination if provided
  const serverPagination: ServerPagination | undefined = pagination
    ? {
      pagination,
      onPageChange: onPageChange || (() => { }),
      onPageSizeChange: onPageSizeChange || (() => { }),
    }
    : undefined

  return (
    <PaginatedTable
      data={sites}
      columns={columns}
      loading={loading}
      error={error}
      onRetry={onRetry}
      serverPagination={serverPagination}
      serverFiltering={
        sort && onSortChange
          ? {
            filters: {},
            sort: sort,
            onFiltersChange: () => { },
            onSortChange: onSortChange,
            onClearAll: () => { },
          }
          : undefined
      }
      emptyState={{
        heading: "No sites",
        text: "Please create a site for this customer",
      }}
      variant="hydrologiq"
      entityName="sites"
      headerActions={headerActions}
    />
  )
}

export default CustomerSitesPaginated
